/**
 * Dialog for loading desktops with list, search, and actions
 */

import React, { useState, useEffect, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog } from "primereact/confirmdialog";
import { Tooltip } from "primereact/tooltip";
import { Checkbox } from "primereact/checkbox";
import PropTypes from "prop-types";
import ModalBase from "../../components/Modals/ModalBase";
import PromptDialog from "./PromptDialog";
import { useSettingsStore } from "../../store/settingsStore";
import { useChartsStore } from "../../store/chartStore";
import useWorkspaceStore, {
  getWorkspaceContext,
} from "../../store/workspaceStore";
import { isFeatureEnabled } from "../../data/Services/settings";
import {
  listDesktopCacheStates,
  isCurrentStateDirty,
} from "../storage/desktopCache";
import { isDesktopRestoreSettling } from "../desktopManager";
import "./DesktopManager.css";

const LoadDesktopDialog = ({
  visible,
  onHide,
  onLoad,
  onDuplicate,
  onRename,
  onDelete,
  onRefresh,
  onReloadFromServer,
}) => {
  const [desktops, setDesktops] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dirtyById, setDirtyById] = useState({});

  // "Meus Desktops": favorites + dirty markers (feature-flag gated at render)
  const myDesktopsEnabled = isFeatureEnabled("myDesktops");
  const favoriteDesktops = useSettingsStore(
    (state) => state.favoriteDesktops || [],
  );
  const toggleFavoriteDesktop = useSettingsStore(
    (state) => state.toggleFavoriteDesktop,
  );
  const currentDesktopId = useWorkspaceStore((state) => state.currentDesktopId);

  // PrimeReact DataTable memoizes its body: favorite/dirty must live IN the
  // row data so a toggle produces a new `value` array and re-renders the rows
  // (templates reading external state render stale otherwise).
  const rows = useMemo(
    () =>
      desktops.map((desktop) => ({
        ...desktop,
        __favorite: myDesktopsEnabled && favoriteDesktops.includes(desktop.id),
        __dirty: myDesktopsEnabled && !!dirtyById[String(desktop.id)],
      })),
    [desktops, favoriteDesktops, dirtyById, myDesktopsEnabled],
  );

  const refreshDirtyStates = () => {
    const { user, broker } = getWorkspaceContext();
    if (!user || !broker) return;
    const states = listDesktopCacheStates(user, broker);
    const map = {};
    for (const [id, state] of Object.entries(states)) {
      map[id] = state.dirty;
    }
    // The active desktop's dirty state is live (cache entry may lag the store)
    if (currentDesktopId != null) {
      map[String(currentDesktopId)] =
        !isDesktopRestoreSettling(currentDesktopId) &&
        isCurrentStateDirty(
          useChartsStore.getState(),
          user,
          broker,
          currentDesktopId,
        );
    }
    setDirtyById(map);
  };

  // Settings
  const autoShowLoadDesktopDialog = useSettingsStore(
    (state) => state.autoShowLoadDesktopDialog,
  );
  const setAutoShowLoadDesktopDialog = useSettingsStore(
    (state) => state.setAutoShowLoadDesktopDialog,
  );

  // Prompt dialog state
  const [promptVisible, setPromptVisible] = useState(false);
  const [promptConfig, setPromptConfig] = useState({
    title: "",
    message: "",
    defaultValue: "",
    placeholder: "",
    onConfirm: null,
  });

  // Load desktops when dialog opens
  useEffect(() => {
    if (visible) {
      loadDesktops();
    }
  }, [visible]);

  const loadDesktops = async () => {
    setLoading(true);
    setError("");

    try {
      const list = await onRefresh();
      setDesktops(list || []);
      if (myDesktopsEnabled) refreshDirtyStates();
    } catch (err) {
      console.error("Error loading desktops:", err);
      setError(err.message || "Falha ao carregar lista de desktops");
      setDesktops([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = async (desktop) => {
    try {
      await onLoad(desktop.id, desktop.name);
      onHide(); // Close dialog after successful load
    } catch (err) {
      console.error("Error loading desktop:", err);
      setError(err.message || "Falha ao carregar desktop");
    }
  };

  const handleDuplicate = (desktop) => {
    // Generate unique name suggestion
    let suggestedName = `${desktop.name} (Cópia)`;
    let counter = 1;

    // Check if name exists and add counter if needed
    while (desktops.some((d) => d.name === suggestedName)) {
      counter++;
      suggestedName = `${desktop.name} (Cópia ${counter})`;
    }

    // Show prompt with suggested name
    setPromptConfig({
      title: "Duplicar Desktop",
      message: `Digite o nome para a duplicata de "${desktop.name}":`,
      defaultValue: suggestedName,
      placeholder: "Nome do desktop",
      onConfirm: async (newName) => {
        // Check if name exists
        const existing = desktops.find(
          (d) => d.name.toLowerCase() === newName.toLowerCase(),
        );
        if (existing) {
          confirmDialog({
            message: `Já existe um desktop com o nome "${newName}". Deseja sobrescrever?`,
            header: "Confirmar Sobrescrita",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-warning",
            acceptLabel: "Sobrescrever",
            rejectLabel: "Cancelar",
            accept: async () => {
              try {
                // Delete existing first, then duplicate with the name
                await onDelete(existing.id);
                await onDuplicate(desktop.id, newName);
                await loadDesktops();
                setPromptVisible(false);
              } catch (err) {
                console.error("Error duplicating desktop:", err);
                setError(err.message || "Falha ao duplicar desktop");
              }
            },
          });
        } else {
          try {
            await onDuplicate(desktop.id, newName);
            await loadDesktops();
            setPromptVisible(false);
          } catch (err) {
            console.error("Error duplicating desktop:", err);
            setError(err.message || "Falha ao duplicar desktop");
          }
        }
      },
    });
    setPromptVisible(true);
  };

  const handleRename = (desktop) => {
    setPromptConfig({
      title: "Renomear Desktop",
      message: `Digite o novo nome para "${desktop.name}":`,
      defaultValue: desktop.name,
      placeholder: "Nome do desktop",
      onConfirm: async (newName) => {
        // Check if name didn't change
        if (newName === desktop.name) {
          setPromptVisible(false);
          return;
        }

        // Check if name exists
        const existing = desktops.find(
          (d) =>
            d.name.toLowerCase() === newName.toLowerCase() &&
            d.id !== desktop.id,
        );

        if (existing) {
          confirmDialog({
            message: `Já existe um desktop com o nome "${newName}". Deseja sobrescrever?`,
            header: "Confirmar Sobrescrita",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-warning",
            acceptLabel: "Sobrescrever",
            rejectLabel: "Cancelar",
            accept: async () => {
              try {
                // Delete existing first, then rename
                await onDelete(existing.id);
                await onRename(desktop.id, newName);
                await loadDesktops();
                setPromptVisible(false);
              } catch (err) {
                console.error("Error renaming desktop:", err);
                setError(err.message || "Falha ao renomear desktop");
              }
            },
          });
        } else {
          try {
            await onRename(desktop.id, newName);
            await loadDesktops();
            setPromptVisible(false);
          } catch (err) {
            console.error("Error renaming desktop:", err);
            setError(err.message || "Falha ao renomear desktop");
          }
        }
      },
    });
    setPromptVisible(true);
  };

  const handleDelete = (desktop) => {
    confirmDialog({
      message: `Tem certeza que deseja excluir o desktop "${desktop.name}"? Esta ação não pode ser desfeita.`,
      header: "Excluir Desktop",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Excluir",
      rejectLabel: "Cancelar",
      accept: async () => {
        try {
          await onDelete(desktop.id);
          await loadDesktops(); // Refresh list
        } catch (err) {
          console.error("Error deleting desktop:", err);
          setError(err.message || "Falha ao excluir desktop");
        }
      },
    });
  };

  const formatDate = (timestamp) => {
    try {
      // Check if timestamp is Unix timestamp (seconds since epoch)
      // If it's a number less than year 2100 in milliseconds, it's likely in seconds
      let date;
      if (typeof timestamp === "number" && timestamp < 4102444800000) {
        // Convert from seconds to milliseconds
        date = new Date(timestamp * 1000);
      } else {
        date = new Date(timestamp);
      }

      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  // Column templates
  const nameTemplate = (rowData) => {
    const isDirty = rowData.__dirty;
    const isFavorite = rowData.__favorite;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.375rem",
          cursor: "pointer",
        }}
        onDoubleClick={() => handleLoad(rowData)}
      >
        {myDesktopsEnabled && (
          <i
            className={`desktop-favorite-star pi ${
              isFavorite ? "pi-star-fill" : "pi-star"
            }`}
            style={{
              fontSize: "12px",
              cursor: "pointer",
              color: isFavorite
                ? "var(--yellow-500, #f5c518)"
                : "var(--text-color-secondary)",
            }}
            data-pr-tooltip={
              isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"
            }
            data-pr-position="top"
            onClick={(e) => {
              e.stopPropagation();
              toggleFavoriteDesktop(rowData.id);
            }}
            data-evo-id={`loaddesktopdialog-star-${rowData.id}`}
          ></i>
        )}
        <i
          className="pi pi-desktop"
          style={{ fontSize: "12px", color: "var(--primary-color)" }}
        ></i>
        <span
          style={{
            fontSize: "12px",
            fontWeight: "500",
            color: "var(--text-color)",
          }}
        >
          {rowData.name}
        </span>
        {isDirty && (
          <span
            className="desktop-dirty-marker"
            style={{
              fontSize: "12px",
              fontWeight: "700",
              color: "var(--primary-color)",
            }}
            data-pr-tooltip="Modificações não salvas"
            data-pr-position="top"
          >
            *
          </span>
        )}
      </div>
    );
  };

  const dateTemplate = (rowData) => {
    return (
      <span
        style={{
          fontSize: "11px",
          color: "var(--text-color-secondary)",
        }}
      >
        {formatDate(rowData.timestamp)}
      </span>
    );
  };

  const handleReloadFromServer = (desktop) => {
    confirmDialog({
      message: `Descartar mudanças locais de "${desktop.name}"?`,
      header: "Recarregar do servidor",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Descartar e recarregar",
      rejectLabel: "Cancelar",
      accept: async () => {
        try {
          await onReloadFromServer?.(desktop.id, desktop.name);
        } catch (err) {
          console.error("Error reloading desktop from server:", err);
          setError(err.message || "Falha ao recarregar desktop do servidor");
        } finally {
          refreshDirtyStates();
        }
      },
    });
  };

  const actionsTemplate = (rowData) => {
    const isDirty = rowData.__dirty;
    return (
      <div style={{ display: "flex", gap: "1px", justifyContent: "center" }}>
        {isDirty && (
          <Button
            icon="pi pi-replay"
            className="p-button-text p-button-sm p-button-warning desktop-action-btn-reload"
            onClick={() => handleReloadFromServer(rowData)}
            data-pr-tooltip="Descartar mudanças locais e recarregar do servidor"
            data-pr-position="top"
            style={{ fontSize: "11px", padding: "4px" }}
            data-evo-id={`loaddesktopdialog-btn-reload-${rowData.id}`}
          />
        )}
        <Button
          icon="pi pi-folder-open"
          className="p-button-text p-button-sm p-button-success desktop-action-btn-load"
          onClick={() => handleLoad(rowData)}
          data-pr-tooltip="Carregar"
          data-pr-position="top"
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id={`loaddesktopdialog-btn-load-${rowData.id}`}
        />
        <Button
          icon="pi pi-copy"
          className="p-button-text p-button-sm desktop-action-btn-duplicate"
          onClick={() => handleDuplicate(rowData)}
          data-pr-tooltip="Duplicar"
          data-pr-position="top"
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id={`loaddesktopdialog-btn-duplicate-${rowData.id}`}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm desktop-action-btn-rename"
          onClick={() => handleRename(rowData)}
          data-pr-tooltip="Renomear"
          data-pr-position="top"
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id={`loaddesktopdialog-btn-rename-${rowData.id}`}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-sm p-button-danger desktop-action-btn-delete"
          onClick={() => handleDelete(rowData)}
          data-pr-tooltip="Excluir"
          data-pr-position="top"
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id={`loaddesktopdialog-btn-delete-${rowData.id}`}
        />
      </div>
    );
  };

  const headerWithSearch = (
    <div className="desktop-dialog-header-inline">
      <span className="desktop-dialog-title">Carregar Desktop</span>
      <div className="desktop-header-search">
        <i className="pi pi-search" />
        <input
          type="text"
          autoComplete="off"
          className="p-inputtext p-component"
          placeholder="Buscar..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          data-evo-id="loaddesktopdialog-input-filter"
        />
      </div>
      <Button
        icon="pi pi-refresh"
        className="p-button-text p-button-sm p-button-rounded desktop-refresh-btn"
        onClick={loadDesktops}
        disabled={loading}
        data-pr-tooltip="Atualizar lista"
        data-pr-position="top"
        style={{ width: "26px", height: "26px", padding: "0" }}
        data-evo-id="loaddesktopdialog-btn-refresh"
      />
    </div>
  );

  const emptyMessage = (
    <div className="desktop-empty-state">
      <i className="pi pi-inbox"></i>
      <p>
        {globalFilter
          ? "Nenhum desktop encontrado com esse nome"
          : "Nenhum desktop salvo encontrado"}
      </p>
    </div>
  );

  return (
    <>
      <Tooltip target=".desktop-action-btn-load" />
      <Tooltip target=".desktop-action-btn-duplicate" />
      <Tooltip target=".desktop-action-btn-rename" />
      <Tooltip target=".desktop-action-btn-delete" />
      <Tooltip target=".desktop-action-btn-reload" />
      <Tooltip target=".desktop-favorite-star" />
      <Tooltip target=".desktop-dirty-marker" />
      <Tooltip target=".desktop-refresh-btn" />

      <ModalBase
        header={headerWithSearch}
        visible={visible}
        onHide={onHide}
        size="large"
        variant="data"
        footer={null}
        className="desktop-load-dialog-modal"
      >
        <div className="desktop-load-dialog">
          {/* Error Message */}
          {error && (
            <Message
              severity="error"
              text={error}
              className="desktop-error-message"
            />
          )}

          {/* Desktop Table */}
          {loading ? (
            <div className="desktop-loading-state">
              <ProgressSpinner style={{ width: "50px", height: "50px" }} />
              <p>Carregando desktops...</p>
            </div>
          ) : (
            <DataTable
              value={rows}
              globalFilter={globalFilter}
              emptyMessage={emptyMessage}
              scrollable
              showGridlines
              stripedRows
              size="small"
              className="desktop-table"
            >
              <Column
                field="name"
                header="Nome"
                body={nameTemplate}
                sortable
                headerClassName="desktop-col-name"
                bodyClassName="desktop-col-name"
              />
              <Column
                field="timestamp"
                header="Data"
                body={dateTemplate}
                sortable
                style={{ width: "130px" }}
                headerClassName="desktop-col-date"
                bodyClassName="desktop-col-date"
              />
              <Column
                header="Ações"
                body={actionsTemplate}
                style={{ width: "120px", textAlign: "center" }}
                headerClassName="desktop-col-actions"
                bodyClassName="desktop-col-actions"
              />
            </DataTable>
          )}

          {/* Auto-show toggle */}
          <div
            className="desktop-dialog-footer-option"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.375rem",
              padding: "0.25rem 0.5rem",
            }}
          >
            <Checkbox
              inputId="autoShowLoadDesktopDialog"
              checked={autoShowLoadDesktopDialog}
              onChange={(e) => setAutoShowLoadDesktopDialog(e.checked)}
              data-evo-id="loaddesktopdialog-checkbox-autoshow"
            />
            <label
              htmlFor="autoShowLoadDesktopDialog"
              style={{
                fontSize: "11px",
                color: "var(--text-color-secondary)",
                cursor: "pointer",
              }}
            >
              Mostrar este diálogo automaticamente ao iniciar
            </label>
          </div>
        </div>
      </ModalBase>

      {/* Prompt Dialog for Rename/Duplicate */}
      <PromptDialog
        visible={promptVisible}
        onHide={() => setPromptVisible(false)}
        onConfirm={promptConfig.onConfirm}
        title={promptConfig.title}
        message={promptConfig.message}
        defaultValue={promptConfig.defaultValue}
        placeholder={promptConfig.placeholder}
      />
    </>
  );
};

LoadDesktopDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onReloadFromServer: PropTypes.func,
};

export default LoadDesktopDialog;
